import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, Row, Col, Badge, Spinner } from 'react-bootstrap';
import './TokenDisplay.css';

const TokenDisplay = () => {
  const [queue, setQueue] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [previousQueue, setPreviousQueue] = useState([]);

  useEffect(() => {
    fetchQueueData();
    const interval = setInterval(fetchQueueData, 5000); // Refresh every 5 seconds
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    
    return () => {
      clearInterval(interval);
      clearInterval(timeInterval);
    };
  }, []);

  useEffect(() => {
    // Check for newly called tokens
    if (previousQueue.length > 0 && queue.length > 0) {
      queue.forEach(newItem => {
        if (newItem.status === 'in-progress') {
          const oldItem = previousQueue.find(old => old.id === newItem.id);
          if (oldItem && oldItem.status === 'waiting') {
            // Token just got called - announce it
            console.log('Announcing token:', newItem.queueNumber);
            announceToken(newItem);
          }
        }
      });
    }
  }, [queue]);

  const fetchQueueData = async () => {
    try {
      const doctorsRes = await axios.get('/api/doctors/active');
      setDoctors(doctorsRes.data);

      const today = new Date().toISOString().split('T')[0];
      const queueRes = await axios.get(`/api/queue?date=${today}`);
      const newQueue = queueRes.data || [];
      
      // Store previous queue before updating
      if (queue.length > 0) {
        setPreviousQueue(queue);
      }
      
      setQueue(newQueue);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching queue:', err);
      setLoading(false);
    }
  };

  const announceToken = (queueItem) => {
    console.log('announceToken called for:', queueItem);
    
    if (!('speechSynthesis' in window)) {
      console.error('Speech synthesis not supported');
      return;
    }
    
    window.speechSynthesis.cancel();
    
    const doctor = doctors.find(d => d.id === queueItem.doctorId);
    const doctorName = doctor ? doctor.name : '';
    const tokenNumber = queueItem.queueNumber;
    const patientName = queueItem.Patient?.name || '';
    
    console.log('Announcing:', { tokenNumber, patientName, doctorName });
    
    const voices = window.speechSynthesis.getVoices();
    const femaleVoice = voices.find(v => v.name.includes('Zira')) || 
                        voices.find(v => v.name.toLowerCase().includes('female')) || 
                        voices.find(v => !v.name.toLowerCase().includes('david') && !v.name.toLowerCase().includes('mark'));
    
    console.log('Selected Voice:', femaleVoice ? femaleVoice.name : 'Default');
    
    const englishText = `Token number ${tokenNumber}. ${patientName}. Please proceed to Doctor ${doctorName}.`;
    const urduText = `Token number ${tokenNumber}. ${patientName}. Doctor ${doctorName} ke paas aayein.`;
      
    const utterance1 = new SpeechSynthesisUtterance(englishText);
    if (femaleVoice) utterance1.voice = femaleVoice;
    utterance1.rate = 0.85;
    utterance1.pitch = 1.5;
    utterance1.volume = 1;
    
    utterance1.onend = () => {
      setTimeout(() => {
        const utterance2 = new SpeechSynthesisUtterance(urduText);
        if (femaleVoice) utterance2.voice = femaleVoice;
        utterance2.rate = 0.8;
        utterance2.pitch = 1.5;
        utterance2.volume = 1;
        
        utterance2.onend = () => {
          setTimeout(() => {
            const repeat1 = new SpeechSynthesisUtterance(englishText);
            if (femaleVoice) repeat1.voice = femaleVoice;
            repeat1.rate = 0.85;
            repeat1.pitch = 1.5;
            repeat1.volume = 1;
            
            repeat1.onend = () => {
              setTimeout(() => {
                const repeat2 = new SpeechSynthesisUtterance(urduText);
                if (femaleVoice) repeat2.voice = femaleVoice;
                repeat2.rate = 0.8;
                repeat2.pitch = 1.5;
                repeat2.volume = 1;
                window.speechSynthesis.speak(repeat2);
              }, 500);
            };
            
            window.speechSynthesis.speak(repeat1);
          }, 1000);
        };
        
        window.speechSynthesis.speak(utterance2);
      }, 500);
    };
    
    window.speechSynthesis.speak(utterance1);
  };

  const getCurrentToken = (doctorId) => {
    return queue.find(q => q.doctorId === doctorId && q.status === 'in-progress');
  };

  const getWaitingTokens = (doctorId) => {
    return queue.filter(q => q.doctorId === doctorId && q.status === 'waiting')
      .sort((a, b) => a.queueNumber - b.queueNumber)
      .slice(0, 3);
  };

  if (loading) {
    return (
      <div className="token-display-loading">
        <Spinner animation="border" variant="primary" size="lg" />
        <h3 className="mt-3">Loading Queue...</h3>
      </div>
    );
  }

  return (
    <div className="token-display-container">
      <div className="token-header">
        <h1>🏥 Patient Queue Status</h1>
        <div className="current-time">
          {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      </div>

      <Row className="g-4">
        {doctors.map(doctor => {
          const currentToken = getCurrentToken(doctor.id);
          const waitingTokens = getWaitingTokens(doctor.id);

          return (
            <Col md={6} key={doctor.id}>
              <Card className="doctor-queue-card">
                <Card.Header className="doctor-header">
                  <h3>Dr. {doctor.name}</h3>
                  <p className="specialization">{doctor.specialization}</p>
                </Card.Header>
                <Card.Body>
                  <div className="current-token-section">
                    <h4>NOW SERVING</h4>
                    {currentToken ? (
                      <div className="current-token">
                        <div className="token-number">#{currentToken.queueNumber}</div>
                        <div className="patient-name">{currentToken.Patient?.name}</div>
                        {currentToken.isEmergency === 1 && (
                          <Badge bg="danger" className="emergency-badge">EMERGENCY</Badge>
                        )}
                      </div>
                    ) : (
                      <div className="no-token">
                        <p>No patient currently</p>
                      </div>
                    )}
                  </div>

                  <div className="waiting-tokens-section">
                    <h5>NEXT IN LINE</h5>
                    {waitingTokens.length > 0 ? (
                      <div className="waiting-tokens">
                        {waitingTokens.map(token => (
                          <div key={token.id} className="waiting-token">
                            <span className="token-num">#{token.queueNumber}</span>
                            <span className="token-patient">{token.Patient?.name}</span>
                            {token.isEmergency === 1 && <Badge bg="danger" className="ms-2">⚠️</Badge>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">No patients waiting</p>
                    )}
                  </div>

                  <div className="queue-stats">
                    <Badge bg="info">
                      Total Waiting: {queue.filter(q => q.doctorId === doctor.id && q.status === 'waiting').length}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      <div className="token-footer">
        <p>Please wait for your token number to be called</p>
        <p className="text-muted">Screen updates automatically every 5 seconds</p>
      </div>
    </div>
  );
};

export default TokenDisplay;
